import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import {
  getInitFormData,
  applyAutoSlip,
  fnGetIotype,
  getBsn,
  fnPrintByReport,
} from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import { showResizablePopupModal } from "@widgets/CommonModal";
import SN_MTL_MTI_REG_013_POP_001 from "@menu/MTL/SN_MTL_MTI_REG_013/SN_MTL_MTI_REG_013_POP_001";
import SnButton from "@shared/components/SnButton";
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";

interface Props {
  Menuprops: menuInfo;
}

const SN_MTL_MTI_REG_013 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
    grid4: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const searchRef = useRef<any>(null);
  const { userInfo, config } = global(); // Login user 정보
  const iotypeRef = useRef<any[]>([]);
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  const [selectedDocu, setSelectedDocu] = useState<string>("");

  const [mas8025, setMAS8025] = useState<string>("");
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
        });

        setMAS8025(config?.MAS8025?.dc_rmk ?? "");
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      onActivate(Menuprops.nodeData);
    }
  }, [columns, Menuprops]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await fnGetIotype("MTL", "2", "6", "");
      iotypeRef.current = data;
    };
    fetchData();
  }, []);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
    onPrint,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const modifiedRowsH = getGridCurrent("grid1").getChange();
    const modifiedRowsL = getGridCurrent("grid2").getChange();
    const modifiedRowsD = getGridCurrent("grid3").getChange();

    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0 ||
      modifiedRowsD.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_mtl_mti_reg_013_01",
        params: searchDataToUse,
      };

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: data[2] || [],
        grid4: data[3] || [],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onActivate
  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      const selectedData = {
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        no_io: nodeData.no_io,
        dt_from: nodeData.dt_io,
        dt_to: nodeData.dt_io,
      };
      const searchData = {
        ...formData,
        ...selectedData,
      };
      setFormData(searchData);
      onSearch(searchData);
    }
  };
  //#endregion

  //#region onInsert
  const onInsert = () => {
    try {
      const addedNode = getGridCurrent("grid1").addRow();
      addedNode?.setDataValue("cd_io", addedNode.data?.cd_io);
    } catch (error: any) {}
  };
  //#endregion

  //#region onAddRow
  const onAddRow = () => {
    try {
      return getGridCurrent("grid2").addRow();
    } catch (error: any) {}
  };
  //#endregion

  //#region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    if (!focusedRowNodeH) {
      await showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    // 삭제할 헤더의 no_io로 출고정보를 필터한다.
    getGridCurrent("grid3").applyFilters({
      no_io: focusedRowNodeH.data.no_io,
    });
    getGridCurrent("grid4").applyFilters({
      no_io: focusedRowNodeH.data.no_io,
    });

    getGridCurrent("grid4").deleteAllRows();
    getGridCurrent("grid3").deleteAllRows();
    getGridCurrent("grid2").deleteAllRows();
    getGridCurrent("grid1").deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      return;
    }

    return await onSave();
  };
  //endregion

  //#region OnDeleteRow
  const onDeleteRow = () => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    getGridCurrent("grid3").deleteAllRows();
    getGridCurrent("grid4").deleteAllRows();

    getGridCurrent("grid2").deleteSelectedRows();
  };
  //#endregion

  //#region onSave
  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();
      getGridCurrent("grid2").stopEditing();
      getGridCurrent("grid4").stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      // L 필수값 체크
      const requiredValuesCheckL =
        getGridCurrent("grid2").requiredValuesCheck();
      if (!requiredValuesCheckL) {
        return false;
      }

      // D 필수값 체크
      const requiredValuesCheckD =
        getGridCurrent("grid4").requiredValuesCheck();
      if (!requiredValuesCheckD) {
        return false;
      }

      const qtiohChanges = getGridCurrent("grid1").getChange();
      const qtioChanges = getGridCurrent("grid2").getChange();
      const qtiolotChanges = getGridCurrent("grid3").getChange(); // 분할원LOT
      const qtiolotChanges2 = getGridCurrent("grid4").getChange(); // 분할상세

      const headerInserts = qtiohChanges.insert ?? [];
      const lineInserts = qtioChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        qtiohChanges.modifiedRowcount === 0 &&
        qtioChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      const noSlip = await applyAutoSlip({
        cd_column: "no_io",
        cd_module: "MTL",
        no_line: 38,
        dt: focusedRowNodeH.data.dt_io,
        girdrefs: [
          gridRefs.current["grid1"],
          gridRefs.current["grid2"],
          gridRefs.current["grid3"],
          gridRefs.current["grid4"],
        ],
        inserts: [qtiohChanges, qtioChanges, qtiolotChanges, qtiolotChanges2],
        cd_menu: Menuprops.cd_menu,
      });

      // grid3 + grid4 머지 (출고/재입고 분기는 백엔드에서 fg_slip으로 처리)
      const qtiolotMerged = {
        insert: [
          ...(qtiolotChanges.insert ?? []),
          ...(qtiolotChanges2.insert ?? []),
        ],
        update: [],
        delete: [
          ...(qtiolotChanges.delete ?? []),
          ...(qtiolotChanges2.delete ?? []),
        ],
        modifiedRowcount:
          qtiolotChanges.modifiedRowcount + qtiolotChanges2.modifiedRowcount,
      };

      // 요청 데이터 구성
      const SaveData = {
        qtioh: qtiohChanges,
        qtio: qtioChanges,
        qtio_lot: qtiolotMerged,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_MTL_MTI_REG_013_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid3").resetUpdate(true);
      getGridCurrent("grid4").resetUpdate(true);
      return false;
    }
  };
  //#endregion

  //#region onPrint
  const onPrint = async () => {
    if (getGridCurrent("grid4").getCheckedRowCount() === 0) {
      await showDialog("alter", "선택된 항목이 없습니다.");
      return;
    } else {
      if (getGridCurrent("grid2").focusedRowNode().data.state === "insert") {
        await showDialog("alter", "저장되지 않은 항목입니다.");
        return;
      }

      const checkedRows = getGridCurrent("grid4").getCheckedRows();

      switch (mas8025) {
        case "EICT":
          const cdItems = checkedRows.map((row: any) => row.cd_item).join(",");
          const noLots = checkedRows.map((row: any) => row.no_lot).join(",");
          const cdWhs = checkedRows.map((row: any) => row.cd_wh).join(",");
          const printParams = {
            cd_firm: userInfo.cd_firm,
            cd_item: cdItems,
            no_barcode: noLots,
            cd_wh: cdWhs,
          };

          await fnPrintByReport("rpt_mtl_bas_reg_007_chem_01", printParams);

          return null;
        default: {
          let cdItem = checkedRows.map((item) => item.cd_item).join(",");
          let dtIo = checkedRows.map((item) => item.dt_io).join(",");
          let qt = checkedRows.map((item) => item.qt_req_inv).join(",");
          let noBarcode = checkedRows.map((item) => item.no_lot).join(",");

          return {
            cd_firm: userInfo.cd_firm,
            cd_item: cdItem,
            dt_io: dtIo,
            qt: qt,
            no_barcode: noBarcode,
          };
        }
      }
    }
  };
  //#endregion

  //#region GridH Events
  const onBeforeAddRowH = (): any => {
    try {
      const modifiedRowsH = getGridCurrent("grid1").getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("warning", "수정된 내용이 있습니다.");
        return false;
      }
      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: searchInfo?.cd_biz,
        cd_dept: userInfo?.cd_dept,
        nm_dept: userInfo?.nm_dept,
        cd_gp: userInfo?.cd_gp,
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        dt_io: today,
        cd_io: "top1",
        fg_trns: "1",
        rt_exch: 1,
        cd_exch: "000",
        tp_um: "011",
        tp_slip: "6",
        fg_slip: "2",
        am: 0,
        am_k: 0,
        am_vat: 0,
        am_supply: 0,
        no_hst: 0,
        no_io: "",
        cd_wh: "",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onCellValueChangedH = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

    switch (colId) {
      case "cd_io":
        const ioType = iotypeRef.current.find(
          (item) => item.cd_io === rowData.cd_io
        );

        rowNode.setDataValue("fg_io", ioType.fg_io, "noEvent");
        rowNode.setDataValue("fg_money", ioType.fg_money, "noEvent");
        rowNode.setDataValue("fg_map", ioType.fg_map, "noEvent");

        if (rowCount > 0) {
          getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
            node.setDataValue(colId, rowData.cd_io, "noEvent");
            node.setDataValue("fg_io", ioType.fg_io, "noEvent");
            node.setDataValue("fg_money", ioType.fg_money, "noEvent");
            node.setDataValue("fg_map", ioType.fg_map, "noEvent");
          });

          getGridCurrent("grid3").forEachNode((node: RowNode) => {
            if (node.data.no_io === rowData.no_io) {
              node.setDataValue(colId, rowData.cd_io, "noEvent");
              node.setDataValue("fg_io", ioType.fg_io, "noEvent");
              node.setDataValue("fg_money", ioType.fg_money, "noEvent");
            }
          });

          getGridCurrent("grid4").forEachNode((node: RowNode) => {
            if (node.data.no_io === rowData.no_io) {
              node.setDataValue(colId, rowData.cd_io, "noEvent");
              node.setDataValue("fg_io", ioType.fg_io, "noEvent");
              node.setDataValue("fg_money", ioType.fg_money, "noEvent");
            }
          });
        }
        break;
      case "dt_io":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue(colId, rowData.dt_io, "noEvent");
        });

        getGridCurrent("grid3").forEachNode((node: RowNode) => {
          if (node.data.no_io === rowData.no_io) {
            node.setDataValue(colId, rowData.dt_io, "noEvent");
          }
        });

        getGridCurrent("grid4").forEachNode((node: RowNode) => {
          if (node.data.no_io === rowData.no_io) {
            node.setDataValue(colId, rowData.dt_io, "noEvent");
          }
        });
        break;
    }
  };

  const onFocusedRowChangedH = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const no_io = selectedRow.no_io;
      getGridCurrent("grid2").selectAndFocusRow(0);
      setSelectedDocu(no_io);

      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();
      if (rowCount === 0) {
        getGridCurrent("grid3").applyFilters({
          no_io: "",
          no_line: "",
        });
        getGridCurrent("grid4").applyFilters({
          no_io: "",
          no_line: "",
        });
      }
    } else {
      return;
    }
  };

  const onGridHBtnsClick = async (id: string) => {
    // 신규행이 없으면 Dialog로 띄우기
    const qtiohChanges = getGridCurrent("grid1").getChange().insert;
    if (qtiohChanges.length === 0) {
      await showDialog("alter", "신규행을 추가하시기 바랍니다.");
      return;
    }

    switch (id) {
      case "btn_POP1":
        // searchPanel에서 선택된 값 전달
        const selectedFormData = searchRef.current.getFormdata();
        const selectedData = {
          cd_biz: selectedFormData.cd_biz,
        };

        // 신규행 포커스 잡아주기
        let focusedRowHIdx = 0;
        getGridCurrent("grid1").forEachFilteredNode((node: RowNode) => {
          if (node.data.state === "insert") {
            focusedRowHIdx = node.rowIndex ?? focusedRowHIdx;
          }
        });

        getGridCurrent("grid1").selectAndFocusRow(focusedRowHIdx);

        const component = (
          <SN_MTL_MTI_REG_013_POP_001 selectedData={selectedData} />
        );
        const result = await showResizablePopupModal({
          title: "재고적용",
          children: component,
        });

        if (!result || result === "close") return;

        const lineResult = result[0];

        // 요청도움창 Line 결과 적용
        lineResult.map((result: any) => {
          const addedNode = onAddRow();
          if (addedNode) {
            for (const key in result) {
              addedNode.setDataValue(key, result[key], "noEvent");
            }
            addedNode.setDataValue("qt_good", result.qt_inv, "noEvent");
            addedNode.setDataValue("qt", result.qt_inv, "noEvent");

            getGridCurrent("grid2").applyBlock(addedNode);

            const newItem = {
              cd_firm: addedNode.data.cd_firm,
              cd_biz: addedNode.data.cd_biz,
              no_io: addedNode.data.no_io,
              no_line: addedNode.data.no_line,
              dt_io: addedNode.data.dt_io,
              tp_slip: addedNode.data.tp_slip,
              fg_slip: addedNode.data.fg_slip,
              fg_io: addedNode.data.fg_io,
              fg_money: addedNode.data.fg_money,
              cd_io: addedNode.data.cd_io,
              cd_item: result.cd_item,
              cd_wh: result.cd_wh,
              no_lot: result.no_lot,
              qt_inv: result.qt_inv,
              qt_good: result.qt_inv,
              dt_io_origin: result.dt_io,
              no_ct: result.no_ct,
              no_plt: result.no_plt,
              no_lot_cust: result.no_lot_cust,
            };
            getGridCurrent("grid3").addRow(newItem);
          }
        });
    }
  };
  //#endregion

  //#region GridL Events
  const onBeforeAddRowL = (): any => {
    try {
      getGridCurrent("grid1").stopEditing();
      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedRowNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      const maxNoLine = getGridCurrent("grid2").getMaxValue("no_line");
      const updatedRow = {
        cd_firm: focusedRowNodeH.data?.cd_firm,
        cd_biz: focusedRowNodeH.data?.cd_biz,
        dt_io: focusedRowNodeH.data?.dt_io,
        no_line: maxNoLine + 1,
        no_io: focusedRowNodeH.data?.no_io,
        cd_io: focusedRowNodeH.data?.cd_io,
        fg_slip: focusedRowNodeH.data?.fg_slip,
        tp_slip: focusedRowNodeH.data?.tp_slip,
        fg_io: focusedRowNodeH.data?.fg_io,
        fg_money: focusedRowNodeH.data?.fg_money,
        fg_vat: "11",
        rt_exch: focusedRowNodeH.data?.rt_exch,
        fg_map: focusedRowNodeH.data?.fg_map,
        qt: 0,
        am: 0,
        am_k: 0,
        am_vat: 0,
        am_supply: 0,
        um: 0,
        um_k: 0,
        um_supply: 0,
        qt_good: 0,
        qt_bad: 0,
        yn_req: "N",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onGridLBtnsClick = async (id: string) => {
    switch (id) {
      case "deleteRow":
        onDeleteRow();
        break;
    }
  };
  //#endregion

  //#region GridD Events
  const onCellValueChangedD = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowNode = event.rowNode;
    const oldValue = event.oldValue;
    const newValue = event.newValue;

    const focusedRowNodeInv = getGridCurrent("grid3").focusedRowNode();

    switch (colId) {
      case "qt_good":
        if (newValue > focusedRowNodeInv.data.qt_inv + oldValue) {
          await showDialog("alter", "재고량보다 크게 입력했습니다.");
          rowNode.setDataValue(colId, oldValue, "noEvent");
        } else {
          focusedRowNodeInv.setDataValue(
            "qt_inv",
            focusedRowNodeInv.data.qt_inv + oldValue - newValue,
            "noEvent"
          );
        }
        break;
    }
  };

  const onGridDBtnsClick = async (id: string) => {
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();
    const focusedRowNodeInv = getGridCurrent("grid3").focusedRowNode();

    if (focusedRowNodeL == null) {
      showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    if (focusedRowNodeL.data.state !== "insert") {
      showDialog("alter", "신규 행에서만 수정 가능합니다.");
      return false;
    }

    if (focusedRowNodeInv == null) {
      showDialog("alter", "선택된 분할대상재고가 없습니다.");
      return false;
    }

    switch (id) {
      case "addRow":
        const newRow = {
          cd_firm: focusedRowNodeL.data?.cd_firm,
          cd_biz: focusedRowNodeL.data?.cd_biz,
          no_io: focusedRowNodeL.data?.no_io,
          no_line: focusedRowNodeL.data?.no_line,
          dt_io: focusedRowNodeL.data?.dt_io,
          tp_slip: focusedRowNodeL.data?.tp_slip,
          fg_slip: "1",
          fg_io: focusedRowNodeL.data?.fg_io,
          fg_money: focusedRowNodeL.data?.fg_money,
          cd_io: focusedRowNodeL.data?.cd_io,
          cd_item: focusedRowNodeL.data?.cd_item,
          cd_wh: focusedRowNodeL.data.cd_wh,
          no_lot: "",
          no_lot_origin: focusedRowNodeInv.data.no_lot,
          qt_good: 0,
          dt_io_origin: focusedRowNodeInv.data.dt_io_origin, // 원 LOT 입고일자
          no_lot_cust: focusedRowNodeInv.data.no_lot_cust, // 원 고객사 LOT
        };

        if (mas8025 === "EICT") {
          // 끝 숫자를 잘라내고 알파벳을 한 단계 올림 (R260907A001 -> R260907B), 채번 접두로 사용
          const lotPrefix = String(newRow.no_lot_origin).replace(
            /([A-Z])\d+$/,
            (_, a) => String.fromCharCode(a.charCodeAt(0) + 1)
          );
          let noSeq = "";

          const noBsn = await getBsn(
            "NOLOTCUST",
            lotPrefix,
            "00000000",
            1,
            1,
            "Y"
          );
          noSeq = String(noBsn.no_serial).padStart(3, "0");
          newRow.no_lot = lotPrefix + noSeq;
        }

        getGridCurrent("grid4").addRow(newRow);
        break;
      case "deleteRow":
        const focusedRowNodeD = getGridCurrent("grid4").focusedRowNode();

        if (focusedRowNodeD) {
          focusedRowNodeInv.setDataValue(
            "qt_inv",
            focusedRowNodeInv.data.qt_inv + focusedRowNodeD.data.qt_good,
            "noEvent"
          );
          getGridCurrent("grid4").deleteSelectedRows();
        }
        break;
    }
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRowH,
          onCellValueChanged: onCellValueChangedH,
          onFocusedRowChanged: onFocusedRowChangedH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onBtnDeleteClick: onGridLBtnsClick,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
      },
      grid4: {
        columns: columns.grid4,
        dataSource: dataSources.grid4,
        events: {
          onCellValueChanged: onCellValueChangedD,
          onBtnAddClick: onGridDBtnsClick,
          onBtnDeleteClick: onGridDBtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="35.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{ rowSelection: "single", title: "분할정보" }}
            events={grids.grid1.events}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_io"] }}
            header={
              <div>
                <SnButton
                  text={"재고적용"}
                  onClick={() => {
                    onGridHBtnsClick("btn_POP1");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="55.5%">
          <div className="flex w-full h-full min-w-0 space-x-1.5">
            <SnResizeBar direction={"width"} id="panel3" orgSize="60%">
              <SnGrid
                ref={gridRefs.current["grid2"]} // Grid Name
                columnConfig={grids.grid2.columns} // 컬럼 설정 전달
                rowData={grids.grid2.dataSource} // Datasource
                etcProps={{ rowSelection: "multiple", title: "상세정보" }}
                events={grids.grid2.events}
                filter={{
                  refs: [gridRefs.current["grid3"], gridRefs.current["grid4"]],
                  columns: ["no_io", "no_line"],
                }}
              />
            </SnResizeBar>
            <div className="flex-1 min-w-0 flex flex-col h-full space-y-1.5">
              <SnResizeBar direction={"height"} id="panel4" orgSize="40%">
                <SnGrid
                  ref={gridRefs.current["grid3"]} // Grid Name
                  columnConfig={grids.grid3.columns} // 컬럼 설정 전달
                  rowData={grids.grid3.dataSource} // Datasource
                  etcProps={{
                    rowSelection: "single",
                    title: "분할대상LOT",
                  }}
                />
              </SnResizeBar>
              <div className="flex-1 min-h-0">
                <SnGrid
                  ref={gridRefs.current["grid4"]} // Grid Name
                  columnConfig={grids.grid4.columns} // 컬럼 설정 전달
                  rowData={grids.grid4.dataSource} // Datasource
                  etcProps={{
                    rowSelection: "single",
                    title: "분할상세정보",
                    ischeckboxSelection: true,
                  }}
                  events={grids.grid4.events}
                />
              </div>
            </div>
          </div>
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_MTI_REG_013;
